import { MONTHS, ROUTE_DOCS_STEWARDSHIP } from '@/common/constants';
import { format } from 'date-fns';
import { REAGION_SOATO } from '@/common/constants/regions';
import { Button } from '@/components/ui/button';
import useStore from '@/lib/store';
import { Download } from 'lucide-react';
import { FC, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  ITableColumn,
  ITableMonthData,
} from '../../../models/IStewardshipTable';
import { useStewardshipStore } from '../../../store/useStewardshipStore';
import { StatisticsCard } from '../../header/StatisticsCard';
import { NavigateTitle } from '../../navigate-title/NavigateTitle';
import { StewardshipTable } from '../../StewardshipTable';
import { TableStewardshipLoading } from '../TableLoading';
import { exportStewardshipToExcel } from '../../../utils/exportStewardshipToExcel';

export const StewardshipRegionList: FC = () => {
  const [searchParams] = useSearchParams();
  const plant_id = searchParams.get('plant_id');
  const organization_id = searchParams.get('organization_id') as string;
  const farmer_type = searchParams.get('farmer_type') as string;
  const { lang } = useParams();
  const navigate = useNavigate();
  const {
    responsibleUsersCount,
    responsibleUsersCountLoading,
    responsibleUsersCountRepublic,
    responsibleUsersCountRepublicLoading,
    republicActionsData,
    republicActionsCachedAt,
    republicActionsLoading,
    fetchResponsibleUsersCount,
    fetchResponsibleUsersCountRepublic,
    fetchRepublicActionsData,
  } = useStewardshipStore();
  const { user, plants } = useStore();
  const { t } = useTranslation();

  if (user?.district_id) {
    navigate(user?.region_id as string);
  }

  if (!!user && user?.district_id) return;

  useEffect(() => {
    if (plant_id) {
      fetchResponsibleUsersCount({ plant_id, organization_id, farmer_type });
      fetchResponsibleUsersCountRepublic({ plant_id, organization_id, farmer_type });
      fetchRepublicActionsData({ plant_id, organization_id, farmer_type });
    }
  }, [plant_id, organization_id, farmer_type]);

  const selectedPlant = useMemo(() => {
    return plants?.find((plant) => plant.id === plant_id);
  }, [plants, plant_id]);

  // Map data to generic table format
  const columns: ITableColumn[] = useMemo(() => {
    return republicActionsData
      .map((region) => ({
        id: region.id,
        name: region.name_uz,
      }))
      .sort((a, b) => {
        const indexA = REAGION_SOATO.findIndex((item) => item.id === a.id);
        const indexB = REAGION_SOATO.findIndex((item) => item.id === b.id);
        return indexA - indexB;
      });
  }, [republicActionsData, user]);

  const headerRows = useMemo(
    () => [
      {
        label: t('stewardship.monitored'),
        getValue: (column: ITableColumn) => {
          const userData = responsibleUsersCountRepublic.find(
            (item) => item.region_id === column.id,
          );
          return userData?.projects_count || '0';
        },
      },
    ],
    [responsibleUsersCountRepublic, t],
  );

  const monthsData: ITableMonthData[] = useMemo(() => {
    const months = new Set<string>();
    republicActionsData.forEach((region) => {
      region.actions_data.forEach((action) => {
        months.add(action.month);
      });
    });

    const sortedMonths = Array.from(months).sort((a, b) => {
      const monthOrder = MONTHS.map((m) => m.value);
      return monthOrder.indexOf(a) - monthOrder.indexOf(b);
    });

    return sortedMonths.map((month) => {
      const actionNames = new Set<string>();
      republicActionsData.forEach((region) => {
        region.actions_data
          .filter((action) => action.month === month)
          .forEach((action) => {
            actionNames.add(action.action_name);
          });
      });

      const actions = Array.from(actionNames).map((actionName) => {
        const columnData: {
          [columnId: string]: {
            responsible_users_count: number;
            completed_actions_count: number;
            pending_actions_count: number;
          };
        } = {};

        columns.forEach((column) => {
          const regionData = republicActionsData.find(
            (item) => item.id === column.id,
          );
          const actionData = regionData?.actions_data.find(
            (action) =>
              action.month === month && action.action_name === actionName,
          );

          const userData = responsibleUsersCountRepublic.find(
            (item) => item.region_id === column.id,
          );

          columnData[column.id] = {
            responsible_users_count: actionData?.responsible_users_count || 0,
            completed_actions_count: actionData?.completed_actions_count || 0,
            pending_actions_count: actionData?.pending_actions_count || 0,
          };
        });

        return {
          action_name: actionName,
          columnData,
        };
      });

      return {
        month,
        actions,
      };
    });
  }, [republicActionsData, columns, responsibleUsersCountRepublic]);

  const handleColumnClick = (column: ITableColumn) => {
    navigate({
      pathname: `/${lang}/${ROUTE_DOCS_STEWARDSHIP}/${column.id}`,
      search: window.location.search,
    });
  };

  const handleExport = async () => {
    if (columns.length > 0) {
      await exportStewardshipToExcel(
        columns,
        headerRows,
        monthsData,
        "mas'ul-xodimlar-respublika",
      );
    }
  };

  return (
    <div className="flex flex-col gap-4 p-8 h-full">
      <div className="flex items-center justify-between flex-shrink-0">
        <div className="flex flex-col">
          <NavigateTitle />
          {republicActionsCachedAt && (
            <p className="text-sm text-gray-500 font-medium">
              {t('data.actual.on', {
                date: format(
                  new Date(republicActionsCachedAt),
                  'yyyy-MM-dd HH:mm',
                ),
              })}
            </p>
          )}
        </div>

        <div className="flex gap-2 items-center">
          {columns.length > 0 && (
            <Button
              onClick={handleExport}
              className="flex items-center gap-2 bg-green-600 hover:bg-green-700 text-white"
            >
              <Download className="size-4" />
              Excel
            </Button>
          )}

          {responsibleUsersCount && selectedPlant && (
            <>
              <StatisticsCard
                icon={null}
                label={t('responsible.users.count')}
                count={Number(responsibleUsersCount.responsible_users_count)}
                countColor="text-green-green-700"
              />
              <StatisticsCard
                icon={null}
                label={t('projects.count')}
                count={Number(responsibleUsersCount.projects_count)}
                countColor="text-dark-blue-300"
              />
            </>
          )}
        </div>
      </div>
      <div className="flex-1 overflow-hidden">
        {responsibleUsersCountLoading ||
          responsibleUsersCountRepublicLoading ||
          republicActionsLoading ? (
          <TableStewardshipLoading />
        ) : (
          <StewardshipTable
            columns={columns}
            headerRows={headerRows}
            monthsData={monthsData}
            onColumnClick={handleColumnClick}
          />
        )}
      </div>
    </div>
  );
};
